import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import InsightCard from "./InsightCard";
import type { Insight } from "@/types/insight";
import { getInsightPath } from "@/utils/insightSlugs";
import { getThumbnailUrl } from "@/utils/imageUtils";

interface SuggestedInsightsProps {
  currentInsightId: string;
  language?: string;
  /**
   * "grid" (default) renders the full 3:2 cards in a responsive grid — used in
   * the bottom section on mobile/tablet. "sidebar" renders a compact vertical
   * list of horizontal items, sized for the sticky right rail on desktop.
   */
  variant?: "grid" | "sidebar";
  /** Sort order: "recent" by publish date (default) or "views" for most-read. */
  sortBy?: "recent" | "views";
  /** Maximum number of insights to fetch. Defaults to 5. */
  limit?: number;
}

const SuggestedInsights = ({ currentInsightId, language = "EN", variant = "grid", sortBy = "recent", limit = 5 }: SuggestedInsightsProps) => {
  const { data: insights, isLoading } = useQuery({
    queryKey: ["suggested-insights", currentInsightId, sortBy, limit],
    queryFn: async () => {
      const orderColumn = sortBy === "views" ? "views_count" : "published_at";
      const { data, error } = await supabase
        .from("insights")
        .select("*")
        .neq("id", currentInsightId)
        .eq("is_published", true)
        .order(orderColumn, { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as Insight[];
    },
  });

  if (isLoading) {
    if (variant === "sidebar") {
      return (
        <div className="flex flex-col gap-5">
          {Array.from({ length: limit }).map((_, i) => (
            <div key={i} className="flex animate-pulse gap-3">
              <div className="min-w-0 flex-1 space-y-2 py-0.5">
                <div className="h-3.5 w-full rounded bg-gray-200" />
                <div className="h-3.5 w-3/4 rounded bg-gray-200" />
                <div className="h-3 w-1/2 rounded bg-gray-200" />
              </div>
              <div className="aspect-[3/2] w-24 shrink-0 rounded-lg bg-gray-200" />
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Array.from({ length: limit }).map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-48 bg-gray-200 rounded-lg mb-4"></div>
            <div className="h-4 w-3/4 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 w-1/2 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!insights?.length) {
    return null;
  }

  if (variant === "sidebar") {
    return (
      <div className="flex flex-col gap-5">
        {insights.map((insight) => {
          const title =
            language === "KO" && insight.title_ko ? insight.title_ko : insight.title_en;
          return (
            <Link
              key={insight.id}
              to={getInsightPath(insight, language)}
              className="group flex gap-3"
            >
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold leading-snug tracking-[-0.01em] text-gray-900 decoration-gray-300 underline-offset-2 group-hover:underline">
                  {title}
                </h3>
                <span className="mt-1 block text-xs text-gray-400">
                  {insight.published_at &&
                    `${format(new Date(insight.published_at), "MMM d, yyyy")} · `}
                  {insight.reading_time || 5} min read
                </span>
              </div>
              {insight.thumbnail_url && (
                <div className="relative aspect-[3/2] w-24 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                  <img
                    src={getThumbnailUrl(insight.thumbnail_url)}
                    alt={title}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
              )}
            </Link>
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {insights.map((insight) => (
        <InsightCard key={insight.id} insight={insight} language={language} />
      ))}
    </div>
  );
};

export default SuggestedInsights;